import { newId, type Category, type Cents } from '@keel/core';
import { CheckCircle2, Plus, Scale, Trash2 } from 'lucide-react';
import { Button, Combobox, IconButton, Input, MoneyInput, MoneyText, NumberInput, type ComboboxProps } from '@/components/ui';
import { cn } from '@/lib/cn';
import { categoryOptions } from './data';
import { absorbRemainder, allocateByPercent, splitRemainder, type SplitDraft } from './splits';

export const newSplitDraft = (amountCents: Cents = 0, categoryId: string | null = null): SplitDraft => ({
  key: newId(), categoryId, amountCents, memo: '', tagIds: [], percent: null,
});

export interface SplitEditorProps {
  /** Signed transaction total the splits must add up to. */
  readonly totalCents: Cents;
  readonly splits: readonly SplitDraft[];
  readonly onChange: (splits: SplitDraft[]) => void;
  readonly categories: readonly Category[];
  readonly currency?: string;
  readonly onCreateCategory?: ComboboxProps['onCreate'];
}

/**
 * Split a transaction across categories. Amounts are signed like the transaction; entering a percentage
 * re-allocates the percent rows with core `allocateCents`, so the parts always sum exactly. The footer says how
 * much is left (or over) and offers to put the remainder on the last line.
 */
export const SplitEditor = ({ totalCents, splits, onChange, categories, currency, onCreateCategory }: SplitEditorProps) => {
  const options = categoryOptions(categories);
  const remainder = splitRemainder(totalCents, splits);
  const sign = totalCents < 0 ? -1 : 1;
  const update = (key: string, patch: Partial<SplitDraft>) => {
    const next = splits.map((s) => (s.key === key ? { ...s, ...patch } : s));
    onChange('percent' in patch ? allocateByPercent(totalCents, next) : next);
  };
  return (
    <fieldset className="flex flex-col gap-3" aria-describedby="split-remainder">
      <legend className="mb-1 text-sm font-medium text-text">Splits</legend>
      {splits.map((s, i) => (
        <div key={s.key} className="grid grid-cols-[1fr_auto] gap-2 rounded-md border border-border p-3 sm:grid-cols-[minmax(0,1fr)_8rem_5.5rem_auto]">
          <Combobox
            aria-label={`Split ${i + 1} category`}
            value={s.categoryId}
            options={options}
            onCreate={onCreateCategory}
            placeholder="Category"
            onChange={(v) => update(s.key, { categoryId: v })}
            className="col-span-2 sm:col-span-1"
          />
          <MoneyInput
            aria-label={`Split ${i + 1} amount`}
            value={Math.abs(s.amountCents)}
            allowNegative={false}
            currency={currency}
            onChange={(c) => update(s.key, { amountCents: sign * (c ?? 0), percent: null })}
          />
          <NumberInput
            aria-label={`Split ${i + 1} percent`}
            value={s.percent ?? null}
            min={0}
            max={100}
            step={1}
            suffix="%"
            placeholder="%"
            onChange={(p) => update(s.key, { percent: p })}
          />
          <IconButton
            aria-label={`Remove split ${i + 1}`}
            icon={<Trash2 aria-hidden />}
            disabled={splits.length <= 1}
            onClick={() => onChange(splits.filter((x) => x.key !== s.key))}
            className="row-start-2 sm:row-start-auto"
          />
          <Input
            aria-label={`Split ${i + 1} memo`}
            placeholder="Memo (optional)"
            value={s.memo}
            onChange={(e) => update(s.key, { memo: e.target.value })}
            className="col-span-2 sm:col-span-4"
          />
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="outline" size="sm" icon={<Plus aria-hidden />} onClick={() => onChange([...splits, newSplitDraft(remainder > 0 === totalCents > 0 ? remainder : 0)])}>
          Add split
        </Button>
        <p
          id="split-remainder"
          aria-live="polite"
          className={cn('flex items-center gap-1.5 text-sm', remainder === 0 ? 'text-positive' : 'text-warning')}
        >
          {remainder === 0 ? (
            <>
              <CheckCircle2 className="size-4" aria-hidden /> Splits add up
            </>
          ) : (
            <>
              <MoneyText cents={Math.abs(remainder)} currency={currency} className="font-medium" />
              {Math.sign(remainder) === sign ? 'left to allocate' : 'more than the total'}
            </>
          )}
        </p>
      </div>
      {remainder !== 0 && splits.length > 0 ? (
        <Button variant="ghost" size="sm" className="self-end" icon={<Scale aria-hidden />} onClick={() => onChange(absorbRemainder(totalCents, splits))}>
          Put the difference on the last split
        </Button>
      ) : null}
    </fieldset>
  );
};
